import React, { useCallback, useEffect } from "react";
import { Divider } from "react-native-paper";
import { FiskalyEstablishmentList } from "./list";
import { FiskalyEstablishmentForm } from "./form";
import {
    GQIntegrationQuery,
    useDeleteEstablishmentMutation,
    useFiskalyEstablishmentsQuery,
    useIntegrationSettingsQuery,
    useSaveFiskalyEstablishmentMutation,
} from "graphql-sdk";
import {
    Alert,
    Loading,
    Spacer,
    StyleFunction,
    useConfirm,
    useForm,
    useThemedStyle,
} from "@venuepos/react-common";
import { FiskalySettings } from "lib";
import { View } from "react-native";
import { useHandleMutationError } from "../../../../hooks";
import {
    FiskalyEstablishmentInput,
    schemaFiskalyEstablishmentInput,
} from "../fiskaly-form";
import { useTranslation } from "locales";

export function FiskalyEstablishmentTab({
    integration,
}: {
    integration: GQIntegrationQuery["integration"];
}) {
    const [t] = useTranslation();
    const styles = useThemedStyle(styleFunc);
    const confirm = useConfirm();
    const integrationSettings = useIntegrationSettingsQuery({
        variables: {
            integrationId: integration.id,
        },
    });
    const [saveEstablishment] = useSaveFiskalyEstablishmentMutation();
    const form = useForm<FiskalyEstablishmentInput>(
        schemaFiskalyEstablishmentInput,
        null
    );
    const [{ values }, { setDefaultValues, setValues, reset }] = form;

    const [deleteEstablishment] = useDeleteEstablishmentMutation();
    const { handleMutationError } = useHandleMutationError();

    const { data, loading, error, refetch } = useFiskalyEstablishmentsQuery({
        variables: {
            integrationId:
                integrationSettings.data.integrationSettings.integrationId,
        },
        fetchPolicy: "no-cache",
    });

    const handleSelectItem = useCallback(
        (est: FiskalyEstablishmentInput) => {
            setValues({
                id: est.id,
                designation: est.designation,
                street: est.street,
                houseNumber: est.houseNumber,
                houseNumberSuffix: est.houseNumberSuffix,
                zip: est.zip,
                town: est.town,
                addressAdditional: est.addressAdditional,
                remarks: est.remarks,
            });
        },
        [setValues]
    );

    const handleClearForm = useCallback(() => {
        reset();
    }, [reset]);

    const handleSubmit = useCallback(async () => {
        if (
            !(integrationSettings?.data?.integrationSettings as FiskalySettings)
                ?.organizationId
        ) {
            return;
        }

        if (!values) {
            return;
        }

        const settings = integrationSettings.data
            .integrationSettings as FiskalySettings;

        await handleMutationError(
            async () => {
                const { id, ...rest } = values;

                await saveEstablishment({
                    variables: {
                        establishmentId: id,
                        establishment: {
                            organizationId: settings.organizationId,
                            integrationId: settings.integrationId,
                            ...rest,
                        },
                    },
                });
            },
            t("common.saved", "Saved"),
            refetch
        );
    }, [
        handleMutationError,
        integrationSettings.data.integrationSettings,
        refetch,
        saveEstablishment,
        t,
        values,
    ]);

    const handleDelete = useCallback(
        async (establishmentId: string) => {
            if (!establishmentId) {
                return;
            }

            if (
                !(await confirm(
                    t(
                        "backoffice.integration.fiskaly.delete_establishment_header",
                        "Delete establishment?"
                    ),
                    t(
                        "backoffice.integration.fiskaly.delete_establishment_body",
                        "If you delete this establishment, you cannot use this for sale anymore.\n\nAre you sure you want to delete this?"
                    )
                ))
            ) {
                return;
            }

            await handleMutationError(
                async () => {
                    await deleteEstablishment({
                        variables: { establishmentId },
                    });
                },
                t("common.deleted", "Deleted"),
                refetch
            );
        },
        [confirm, deleteEstablishment, handleMutationError, refetch, t]
    );

    useEffect(() => {
        if (!integrationSettings?.data?.integrationSettings) {
            return;
        }

        if (!values) {
            setDefaultValues({
                designation: "",
                houseNumber: "",
                street: "",
                zip: "",
                town: "",
                houseNumberSuffix: "",
                addressAdditional: "",
                remarks: "",
            });
        }
    }, [
        integration,
        integration.id,
        integrationSettings,
        setDefaultValues,
        values,
    ]);

    if (!integrationSettings?.data?.integrationSettings) {
        return;
    }

    return (
        <View>
            <View style={styles.container}>
                {integrationSettings.error ? (
                    <Alert type="error">
                        {t(
                            "backoffice.error.from_server",
                            "There was an error: {{errorText}}",
                            {
                                errorText: error?.message,
                            }
                        )}
                    </Alert>
                ) : (
                    <FiskalyEstablishmentForm
                        form={form}
                        onSubmit={handleSubmit}
                        onClear={handleClearForm}
                    />
                )}
            </View>
            <Spacer />
            <Divider />
            <Spacer />
            {error ? (
                <Alert type="error">
                    {t(
                        "backoffice.error.from_server",
                        "There was an error: {{errorText}}",
                        {
                            errorText: error?.message,
                        }
                    )}
                </Alert>
            ) : loading ? (
                <Loading />
            ) : (
                <FiskalyEstablishmentList
                    establishments={data?.fiskalyEstablishments.data}
                    onEdit={handleSelectItem}
                    onDelete={handleDelete}
                />
            )}
        </View>
    );
}

const styleFunc: StyleFunction = theme => ({
    container: {
        maxWidth: theme.dimensions.maxFormWidth,
        gap: 10,
    },
});
